import { useState, useEffect,useRef } from "react";
import { Form, Dropdown, Card, Badge } from "react-bootstrap";
import ErrorComponent from "../../../Components/ErrorMessage";
import { replacePlaceholder } from "../../../Utils/Utils";
import './multiselectiondropdown.css';
const MultiselectDropdown = ({
  question,
  formValues,
  showError,
  setFormValues,
  handleChange,
  getChoiceOrder,
  setChoiceOrder,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [baseChoices, setBaseChoices] = useState([]);

  const isMandatory = question.isMandatory;
  const hasAnswer = formValues[question.tempId];
  const questionText = replacePlaceholder(question.questionText, formValues);
  const questionKey = question.tempId;

  /* ---------- RANDOMIZE refs ---------- */
  const seedRef = useRef({});

  /* ---------- keep selected in sync ---------- */
  useEffect(() => {
    if (formValues[questionKey]) {
      setSelectedChoices([...formValues[questionKey]]);
    } else {
      setSelectedChoices([]);
    }
  }, [formValues, questionKey]);

  /* ---------- RANDOMIZE ONCE + FREEZE ---------- */
 useEffect(() => {
  // do not rerun once initialized
  if (baseChoices.length > 0) return;

  const frozen = getChoiceOrder?.(questionKey);
  if (frozen) {
    setBaseChoices(frozen);
    return;
  }

  let choices = [...question.choices];
  if (!hasAnswer) {
    console.log(question.choicesOrder , "order")
    if (question.choicesOrder === "random") {
      if (!seedRef.current[questionKey]) {
        seedRef.current[questionKey] = Math.random();
      }

      let random = seedRef.current[questionKey];
      for (let i = choices.length - 1; i > 0; i--) {
        random = (random * 9301 + 49297) % 233280;
        const j = Math.floor((random / 233280) * (i + 1));
        [choices[i], choices[j]] = [choices[j], choices[i]];
      }
     
    } else if (question.choicesOrder === "ascending") {
      choices.sort((a, b) => a.text.localeCompare(b.text));
    } else if (question.choicesOrder === "descending") {
      choices.sort((a, b) => b.text.localeCompare(a.text));
    }
  }

  setBaseChoices(choices);
}, [
  questionKey,
  question.choices,
  question.choicesOrder,
  hasAnswer,          //  include this
  baseChoices.length  //  include this
]);


  /* ---------- freeze order on first interaction ---------- */
  const freezeOrder = () => {
    if (!getChoiceOrder?.(questionKey) && setChoiceOrder && baseChoices && baseChoices.length > 0) {
      setChoiceOrder(questionKey, baseChoices);
    }
  };

  /* ---------- selection handlers (UNCHANGED) ---------- */
  const handleSelectOption = (event) => {
    freezeOrder();

    const selectedValue = event.target.value;
    const selectedArray = [...selectedChoices];

    if (selectedArray.includes(selectedValue)) {
      selectedArray.splice(selectedArray.indexOf(selectedValue), 1);
    } else {
      selectedArray.push(selectedValue);
    }

    setSelectedChoices(selectedArray);
    setFormValues({ ...formValues, [questionKey]: selectedArray });
  };

  const handleRemoveOption = (selected) => {
    const selectedArray = [...selectedChoices];
    selectedArray.splice(selectedArray.indexOf(selected), 1);
    setSelectedChoices(selectedArray);
    setFormValues({ ...formValues, [questionKey]: selectedArray });
  };

  const handleToggleDropdown = () => {
    setSearchTerm("");
    // freeze randomized order when user opens the dropdown so it stays stable
    if (!showDropdown) {
      freezeOrder();
    }
    setShowDropdown(!showDropdown);
  };

  /* ---------- selected-first ordering (UNCHANGED) ---------- */
  const selectedSet = new Set(selectedChoices);

  const orderedChoices = baseChoices.reduce(
    (acc, choice) => {
      if (selectedSet.has(choice.value)) acc.selected.push(choice);
      else acc.unselected.push(choice);
      return acc;
    },
    { selected: [], unselected: [] }
  );

  const allChoices = orderedChoices.selected.concat(orderedChoices.unselected);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const maxWebChoices = isMobile ? 0 : 5;

  return (
    <Card className={`mt-3 ${isMandatory && showError && !hasAnswer ? "border-danger" : ""}`}>
      <Card.Body>
        <div className="flex-column">
          <Form.Label>
            <b style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {question.tempId}.{questionText}
              {isMandatory && <span className="text-danger">*</span>}
            </b>
          </Form.Label>
          <Form.Label>
            <b style={{ color: "lightblue" }}>{question.questionDescription}</b>
          </Form.Label>
          {isMandatory && showError && !hasAnswer && (
            <ErrorComponent message={question.requiredErrorText} />
          )}
        </div>

        <div className="multiselect-wrapper">
          <Dropdown
            className="multiselect-dropdown"
            show={showDropdown}
            onToggle={handleToggleDropdown}
          >
            <Dropdown.Toggle
              variant="secondary"
              className="w-100"
              id={`${questionKey}-dropdown`}
              style={{
                backgroundColor: "white",
                color: "black",
                textAlign: "left",
              }}
            >
              {selectedChoices.length === 0 && <span>Select an option</span>}

              {selectedChoices.length > 0 && selectedChoices.length <= maxWebChoices && (
                <span>
                  {selectedChoices.map((selected) => (
                    <Badge
                      key={selected}
                      variant="secondary"
                      className="mr-1"
                      onClick={() => handleRemoveOption(selected)}
                    >
                      {selected}
                    </Badge>
                  ))}
                </span>
              )}

              {selectedChoices.length > maxWebChoices && (
                <span>
                  {isMobile
                    ? `${selectedChoices.length} items selected`
                    : `+${selectedChoices.length} selected`}
                </span>
              )}
            </Dropdown.Toggle>

            <Dropdown.Menu className="w-100" style={{ maxHeight: 300, overflowY: "auto" }}>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Search..."
                  className="form-control"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {allChoices
                .filter((c) =>
                  c.text.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((choice) => (
                  <Form.Check
                    key={choice.value}
                    type="checkbox"
                    label={choice.text}
                    value={choice.value}
                    checked={selectedChoices.includes(choice.value)}
                    onChange={handleSelectOption}
                  />
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Card.Body>
    </Card>
  );
};


export default MultiselectDropdown;

